/**
 * Created by Thomas Mark
 * Date: 2020-05-01
 * 
 * ImageCard - Hier wird ein einzelne Bild der ImageGallery angezeigt.
 * 
 * Ist die Applikation im Admin-Modus, dann stehen folgende Funktionen zur Verfügung:
 * - onDelete - Löscht ein einzelnes Bild
 * - onClick - Öffnet den ImageGalleryModal-Dialog
 * - drag & drop - Ermöglicht die Verschiebung der Reihenfolge der Bilder
 *  
 */
import React from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useDrag, useDrop } from "react-dnd";
import { FabIconDelete } from "../../partials/Icons";
import { bindActionCreators } from 'redux';

import * as appActions from "../../../store/actions/appActions";
import * as fileActions from "../../../store/actions/fileActions";

import * as utils from "../../../basics/utils";
import * as cordova from "../../../basics/cordova";

const ItemType = "IMAGE";

const ImageCard = ({ image, index, moveImage, onReorderImage, onDeleteImage }) => {
    const app = useSelector((state) => state.app);
    const { inputHeight, orientation, appModus, path, gallerySearch } = app;

    const setting = useSelector((state) => state.setting);
    const { tagSettings } = setting;

    const dispatch = useDispatch();
    const { setModalDialog, setGalleryIndex } = bindActionCreators(appActions, dispatch);
    const { deleteFile } = bindActionCreators(fileActions, dispatch);

    const itemHeight = (orientation === 'landscape') ? inputHeight / 6 : inputHeight / 8;

    const ref = React.useRef(null);

    // ==========================================================================
    // 
    // HELPER SECTION
    // 
    // ==========================================================================

    const getImageUrl = (path, file) => {
        if (appModus === 'Admin') {
            return utils.getURL('product', path, file);

        } else {
            const data = cordova.getImageFromBuffer(path, file);
            return window.URL.createObjectURL(data);
        }
    }

    const [, drop] = useDrop({
        accept: ItemType,
        hover(item) {
            if (item.index !== index) {
                moveImage(item.index, index);
                item.index = index;
            }
        },
        drop: (item, monitor) => {
            onReorderImage(item.index);
        },
    });

    const [, drag] = useDrag({
        type: ItemType,
        item: { index }
    });

    // ==========================================================================
    // 
    // CALLBACK SECTION
    // 
    // ==========================================================================
    const onClick = () => {
        setGalleryIndex(index);
        if (appModus !== 'Admin') {
            setModalDialog('ImageGalleryModal', '');
        } else {
            setModalDialog('ImageTagsModal');
        }
    }

    const onDelete = () => {
        const onDeleteSuccess = () => {

            onDeleteImage(image.file);
        }

        const onDeleteError = (error) => {
            console.log('onDelete() error:', error);
        }

        deleteFile('product', path, image.file, onDeleteSuccess, onDeleteError);
    }

    // ==========================================================================
    // 
    // SUB RENDER
    // 
    // ==========================================================================

    /**
     * Button "Schlagwort löschen" anzeigen 
     */
    const renderDeleteButton = () => {
        if (appModus !== 'Admin') return null;

        return FabIconDelete("Bild entfernen", onDelete)
    };

    /**
     * Einzelnes Schlagwort anzeigen 
     */
    const renderTag = (d, i) => {

        const checkFilter = (filter) => {

            const filterResult = image?.tags?.filter(value => value === filter)

            return filterResult?.length > 0 ? true : false;
        }

        const exists = checkFilter(d);

        if (!exists) return null;

        const styleStandard = { backgroundColor: 'rgb( 64, 66,  65)', color: 'white' };

        const styleSelected = { backgroundColor: 'rgb(58, 133, 247)', color: 'white' };
        const style = Object.assign(exists ? styleSelected : styleStandard, utils.getStyleHeight(itemHeight, .1, .05, .05));

        return (
            <div key={i} className="inline-block bg-gray-200 rounded px-1 py-1 mr-1 whitespace-no-wrap overflow-hidden"
                style={style}>{d}
            </div>
        )
    }

    /**
     * Alle Schlagwörter anzeigen 
     */
    const renderImageTags = () => {
        if (appModus === 'Admin') return null;

        if (!gallerySearch) return null;
        if (!tagSettings['image']) return null;

        return (
            <div className="absolute bottom-2 p-2" >
                {tagSettings.image.map((d, i) => renderTag(d, i))}
            </div>
        )
    }

    // ==========================================================================
    // 
    // MAIN RENDER
    // 
    // ==========================================================================

    if (appModus === 'Admin') {
        drag(drop(ref));
    }

    return (
        <div ref={ref} className='relative flex py-2 my-2 p-1' >
            <div onClick={onClick}>
                <img src={getImageUrl(path, image.file)} alt="" style={{ minWidth: itemHeight, height: itemHeight, objectFit: "cover" }} />
            </div>
            {renderImageTags()}
            {renderDeleteButton()}
        </div>
    );
};

export default ImageCard;


